import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import { useCreateSession } from '../../hooks/useSessions';
import { buildOneOffStartAt } from '../../lib/oneOffSession';
import { DatePicker } from '../../components/pickers/DatePicker';
import { useShowTeacher, useTeacherOptions } from '../../hooks/useTeachers';
import { useMembers } from '../../hooks/useTeam';
import { useAuthStore } from '../../stores/authStore';
import { TeacherListEditor, type TeacherListRow } from '../../components/TeacherListEditor';

export function OneOffSessionForm({ enrollmentIds, subjectId, onCreated, initialDate, lockDate }: {
  /** Everyone attending the new buổi. `POST /sessions` already takes a list and
   *  finds-or-joins on (subjectId, startAt), so two students in one call means
   *  one session with two participants — not two sessions. */
  enrollmentIds: string[]; subjectId: string; onCreated: () => void;
  /** Prefills the date. Omitted (AssignDialog) the field starts empty as before. */
  initialDate?: string;
  /** The date is fixed by the caller's context — the workbench adds the buổi
   *  to the day it is showing — so the picker is rendered read-only rather
   *  than left to disagree with the screen around it. */
  lockDate?: boolean;
}) {
  const { t } = useTranslation('scheduling');
  const create = useCreateSession();
  const showTeacher = useShowTeacher();
  const teacherOptions = useTeacherOptions();
  const { data: members } = useMembers();
  const authUser = useAuthStore((s) => s.user);
  const [date, setDate] = useState(initialDate ?? '');
  const [time, setTime] = useState('18:00');
  const [durationMin, setDurationMin] = useState(60);
  const [teacherId, setTeacherId] = useState('');
  const [teachers, setTeachers] = useState<TeacherListRow[]>([]);
  const canSubmit = enrollmentIds.length > 0 && date !== '' && time !== '' && durationMin > 0;

  // Same fallback createSlotSchema's teacherId default uses, made explicit
  // here because a one-off session (unlike a slot) always has a picker
  // visible — an empty Select would be its own bug. Current user's own
  // membership if they teach, else the org's first teacher.
  const defaultTeacherId = useMemo(
    () => members?.find((m) => m.user.id === authUser?.id)?.id ?? teacherOptions[0]?.id ?? '',
    [members, authUser, teacherOptions],
  );
  const effectiveTeacherId = teacherId || defaultTeacherId;

  const submit = () => {
    create.mutate(
      {
        subjectId, startAt: buildOneOffStartAt(date, time), durationMin, enrollmentIds,
        ...(showTeacher && effectiveTeacherId ? { teacherId: effectiveTeacherId } : {}),
        ...(showTeacher && teachers.length > 0
          ? { teachers: teachers.map((r) => ({ membershipId: r.membershipId, role: r.role })) }
          : {}),
      },
      // Back to the caller's starting point, not to blank: a locked date is
      // still the right date for a second buổi added on the same day.
      { onSuccess: () => { setDate(initialDate ?? ''); setTeachers([]); onCreated(); } },
    );
  };

  return (
    <div className="grid gap-2">
      <div className="grid grid-cols-3 gap-2">
        <label className="text-sm grid gap-1">{t('oneOff.date')}<DatePicker value={date} onChange={setDate} disabled={lockDate} className="w-full" /></label>
        <label className="text-sm grid gap-1">{t('oneOff.time')}<Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
        <label className="text-sm grid gap-1">{t('oneOff.duration')}<Input type="number" min={15} step={15} value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} /></label>
      </div>
      {showTeacher && (
        <div className="grid gap-1.5">
          <Label className="text-sm">{t('slot.teacherOwner')}</Label>
          <Select value={effectiveTeacherId} onValueChange={setTeacherId}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {teacherOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
                    {opt.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Compact: no override disclosure — a one-off session has no rate
              of any kind, same as SessionDetailPage's own list editor. */}
          <TeacherListEditor
            ownerTeacherId={effectiveTeacherId}
            rows={teachers}
            onChange={setTeachers}
            options={teacherOptions}
            showOverride={false}
            mode="session"
            ns="scheduling"
          />
        </div>
      )}
      <Button disabled={!canSubmit || create.isPending} onClick={submit}>{t('oneOff.add')}</Button>
    </div>
  );
}
